<!-- eslint-disable vue/no-v-html -->
<template>
    <div
        ref="container"
        class="document-artifact"
        :class="{ inline: isInline }">
        <!-- eslint-disable vue/no-unused-refs -->
        <div
            ref="content"
            class="rendered-markdown"
            v-html="parsedContent" />
        <ask-pendo-mention-popover
            v-if="showMentionPopover"
            :mention-data="mentionData"
            :reference-element="popoverReferenceElement"
            :segment-dependents="segmentDependents"
            @mouseenter="onPopoverMouseEnter"
            @mouseleave="onPopoverMouseLeave" />
    </div>
</template>
<script>
import { renderMarkdown } from '@/common/marked/markdown.utils.js';
import { mapActions, mapState } from 'vuex';
import { PendoNotification } from '@pendo/components';
import { templateRef, useElementSize } from '@vueuse/core';
import { printDocument } from '@/ask-pendo/artifacts/document-artifact/print-document';
import AskPendoMentionPopover from '@/ask-pendo/components/ask-pendo-mention-popover.vue';
import mentionPopoverMixin from '@/ask-pendo/utils/mention-popover.mixin.js';

export default {
    name: 'DocumentArtifact',
    components: {
        AskPendoMentionPopover,
    },
    mixins: [mentionPopoverMixin],
    props: {
        artifactId: {
            type: String,
            default: null,
        },
        data: {
            type: Object,
            default: null,
        },
        isInline: {
            type: Boolean,
            default: false,
        },
        isStreaming: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: 'Document',
        },
    },
    setup () {
        const content = templateRef('content');
        const { height: contentHeight } = useElementSize(content);

        return {
            contentHeight,
        };
    },
    computed: {
        ...mapState({
            segmentDependents: (state) => state.segments.segmentDependents,
        }),
        parsedContent () {
            if (!this.data?.content) return '';

            return renderMarkdown(this.data.content);
        },
        // Used by mentionPopoverMixin
        // eslint-disable-next-line internal-rules/no-unused-properties
        mentionPopoverDisabled () {
            return this.isInline;
        },
    },
    watch: {
        contentHeight () {
            if (!this.$refs.container) return;

            const { scrollTop, scrollHeight, clientHeight } = this.$refs.container;
            const nearBottom = scrollTop + clientHeight > scrollHeight - 100;

            if (this.isStreaming && nearBottom) {
                this.scrollToBottom();
            }
        },
    },
    beforeUnmount () {
        this.clearArtifactMenuItems(this.artifactId);
    },
    mounted () {
        if (this.isStreaming) {
            this.scrollToBottom();
        }

        this.setArtifactMenuItems({
            artifactId: this.artifactId,
            items: [
                {
                    id: 'download-pdf',
                    icon: 'download',
                    label: this.$t('askPendo.documentArtifact.Save as PDF'),
                    onClick: () => {
                        this.handleDownloadPDF();
                    },
                },
                {
                    id: 'copy',
                    icon: 'copy',
                    label: this.$t('askPendo.documentArtifact.Copy'),
                    onClick: () => {
                        this.handleCopy();
                    },
                },
            ],
        });
    },
    methods: {
        ...mapActions('askPendo', ['clearArtifactMenuItems', 'setArtifactMenuItems']),
        // Used by mentionPopoverMixin
        // eslint-disable-next-line internal-rules/no-unused-properties
        getMentionPopoverContainer () {
            return this.$refs.content;
        },
        async handleCopy () {
            if (navigator && navigator.clipboard) {
                try {
                    const content = this.data?.content || '';

                    const clipboardItem = new ClipboardItem({
                        'text/html': new Blob([renderMarkdown(content)], { type: 'text/html' }),
                        'text/plain': new Blob([renderMarkdown(content, 'plaintext')], { type: 'text/plain' }),
                    });

                    await navigator.clipboard.write([clipboardItem]);
                    PendoNotification.success({
                        message: this.$t('askPendo.documentArtifact.Copied to clipboard'),
                    });
                } catch {
                    PendoNotification.error({
                        message: this.$t('askPendo.documentArtifact.Failed to copy to clipboard'),
                    });
                }
            }
        },
        async handleDownloadPDF () {
            const content = this.data?.content;
            if (!content) return;

            await printDocument(content, this.name);
        },
        scrollToBottom () {
            this.$nextTick(() => {
                if (this.$refs.container) {
                    this.$refs.container.scrollTop = this.$refs.container.scrollHeight;
                }
            });
        },
    },
};
</script>
<style scoped lang="less">
@import "@/common/marked/markdown-styles";
.document-artifact {
    height: 100%;
    width: 100%;
    padding: 40px;
    overflow-y: auto;
    background: var(--palette-gray-0);

    &.inline {
        max-height: 400px;
        mask-image: linear-gradient(to bottom, black calc(100% - 75px), transparent 100%);

        :deep(.rendered-markdown span.mention:hover) {
            background-color: var(--palette-gray-10);
        }
    }
}

:deep(.rendered-markdown) {
    .markdown-document-styles();
}
</style>